/**
 * Dev-only console filter for React deprecation warnings emitted by
 * third-party libraries we cannot fix in place:
 *
 * - semantic-ui-react 2.x — defaultProps on function components, and
 *   findDOMNode via its <Ref> implementation (fix only exists in the
 *   unreleased v3)
 * - react-markdown 3.x — defaultProps (fixed in v9+, see
 *   docs/migrations/react-markdown.md)
 * - react-datepicker 1.x — unsafe lifecycles, legacy context via its
 *   bundled react-popper, and a javascript: URL on its clear button
 *
 * Until those migrations happen (see docs/migrations/react-19-upgrade.md)
 * this filters those exact warnings for the allowlisted library components
 * only — the same warnings raised for first-party components still surface.
 *
 * Remove this file as part of the React 19 upgrade.
 */
import * as semanticUiReact from 'semantic-ui-react';

const DEFAULT_PROPS_WARNING =
    'Support for defaultProps will be removed from function components';
const FIND_DOM_NODE_WARNING = 'findDOMNode is deprecated';
const LEGACY_CONTEXT_WARNING =
    /uses the legacy (childContextTypes|contextTypes) API/;
const UNSAFE_LIFECYCLE_WARNING =
    'has been renamed, and is not recommended for use';
const JAVASCRIPT_URL_WARNING =
    'will block javascript: URLs as a security precaution';

// react-popper 0.x internals, bundled inside react-datepicker 1.x.
const REACT_POPPER_COMPONENTS = new Set(['Manager', 'Popper', 'Target']);

// Library components that trigger the unsafe-lifecycle warning.
const LIBRARY_LIFECYCLE_COMPONENTS = new Set(['DatePicker']);

export function suppressLibraryDeprecationWarnings() {
    if (process.env.NODE_ENV === 'production') {
        return;
    }

    const defaultPropsComponentNames = new Set([
        ...Object.keys(semanticUiReact),
        // semantic-ui-react internals not in its public exports
        // (@fluentui/react-component-event-listener, used by Sidebar etc.)
        'EventListener',
        'ReactMarkdown',
    ]);

    const containsText = (args: unknown[], text: string) =>
        args.some((arg) => typeof arg === 'string' && arg.includes(text));

    const shouldSuppress = (args: unknown[]) => {
        const [format, detail] = args;
        if (typeof format !== 'string') {
            return false;
        }
        if (format.includes(DEFAULT_PROPS_WARNING)) {
            return (
                typeof detail === 'string' &&
                defaultPropsComponentNames.has(detail)
            );
        }
        if (format.includes(FIND_DOM_NODE_WARNING)) {
            // RefFindNode is semantic-ui-react's <Ref> implementation.
            return containsText(args, 'RefFindNode');
        }
        if (LEGACY_CONTEXT_WARNING.test(format)) {
            return (
                typeof detail === 'string' &&
                REACT_POPPER_COMPONENTS.has(detail) &&
                containsText(args, 'DatePicker')
            );
        }
        if (format.includes(JAVASCRIPT_URL_WARNING)) {
            // react-datepicker 1.x's clear button is an
            // <a href="javascript:void(0)"> — only suppress that exact URL
            // when DatePicker is in the component stack.
            return (
                containsText(args, 'javascript:void(0)') &&
                containsText(args, 'DatePicker')
            );
        }
        if (format.includes(UNSAFE_LIFECYCLE_WARNING)) {
            // detail is "Please update the following components: <names>"'s
            // comma-separated name list — only suppress when every named
            // component is a known library one, so a batch that includes a
            // first-party component still surfaces.
            return (
                typeof detail === 'string' &&
                detail
                    .split(',')
                    .every((name) =>
                        LIBRARY_LIFECYCLE_COMPONENTS.has(name.trim()),
                    )
            );
        }
        return false;
    };

    /* eslint-disable no-console -- this module exists to wrap the console */
    const originalConsoleError = console.error;
    console.error = (...args: unknown[]) => {
        if (shouldSuppress(args)) {
            return;
        }
        originalConsoleError(...args);
    };
    const originalConsoleWarn = console.warn;
    console.warn = (...args: unknown[]) => {
        if (shouldSuppress(args)) {
            return;
        }
        originalConsoleWarn(...args);
    };
    /* eslint-enable no-console */
}
